import React, { useState, useEffect, useCallback } from 'react';
import { gamesApi, seatsApi, blindsApi } from '../services/api';
import './GameTable.css';

function GameTable({ username, userId, userLocation, selectedSeat, setSelectedSeat, game, onLogout }) {
  const [players, setPlayers] = useState([]);
  const [tables, setTables] = useState([]);
  const [gameInfo, setGameInfo] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reserving, setReserving] = useState(false);

  // Blind timer state
  const [gameStarted, setGameStarted] = useState(false);
  const [currentBlind, setCurrentBlind] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isBreak, setIsBreak] = useState(false);

  const totalSeats = 10; // 10 player seats around the table

  // Format time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Format player name as "FirstName L."
  const formatPlayerName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1][0];
    return `${firstName} ${lastInitial}.`;
  };

  // Fetch seating data
  useEffect(() => {
    if (!game?.id) return;

    const fetchSeating = async () => {
      try {
        setLoading(true);
        const response = await gamesApi.getGameSeating(game.id);

        if (response.success) {
          setGameInfo(response.data.game);
          setTables(response.data.tables);

          // Flatten players from all tables
          const allPlayers = [];
          response.data.tables.forEach((table) => {
            table.seats.forEach((seat) => {
              if (seat.isOccupied) {
                allPlayers.push({
                  id: seat.playerId,
                  name: seat.playerName,
                  seat: seat.seatNumber,
                  tableId: table.tableId,
                  avatar: '👤',
                });
              }
            });
          });
          setPlayers(allPlayers);
        }
      } catch (err) {
        console.error('Error fetching seating:', err);
        setError(err.message || 'Failed to load seating');
      } finally {
        setLoading(false);
      }
    };

    fetchSeating();
  }, [game?.id]);

  // Fetch blind timer status
  useEffect(() => {
    if (!game?.id) return;

    const fetchBlindTimer = async () => {
      try {
        const response = await blindsApi.getBlindTimer(game.id);
        if (response.success) {
          const { timer, currentBlind: blind } = response.data;

          if (timer) {
            const isRunning = timer.timerStatus === 'running' || timer.timerStatus === 'paused';
            setGameStarted(isRunning);
            setCurrentLevel(timer.currentLevel || 1);
            setTimeRemaining(timer.timeRemaining || 0);
            setIsPaused(timer.timerStatus === 'paused');

            if (blind) {
              setCurrentBlind(blind);
              setIsBreak(blind.isBreak || false);
            }
          } else {
            setGameStarted(false);
          }
        }
      } catch (err) {
        // Silently fail - game may not have blind timer set up yet
        console.log('Blind timer not available:', err.message);
      }
    };

    // Initial fetch
    fetchBlindTimer();

    // Poll every 5 seconds to get updates from dealer
    const pollInterval = setInterval(fetchBlindTimer, 5000);

    return () => clearInterval(pollInterval);
  }, [game?.id]);

  // Local countdown timer (syncs with server every 5 seconds)
  useEffect(() => {
    if (!gameStarted || isPaused) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, isPaused]);

  const handleSeatClick = async (seatNumber) => {
    const isSeatTaken = players.some((player) => player.seat === seatNumber);

    if (isSeatTaken || reserving) {
      return;
    }

    // Debug logging for seat reservation parameters
    console.log('=== Seat Reservation Debug ===');
    console.log('game:', game);
    console.log('game.id:', game?.id);
    console.log('userId:', userId);
    console.log('userLocation:', userLocation);
    console.log('userLocation.lat:', userLocation?.lat);
    console.log('userLocation.lon:', userLocation?.lon);
    console.log('seatNumber:', seatNumber);
    console.log('==============================');

    // Validate all required parameters with specific error messages
    const missingParams = [];
    if (!game?.id) missingParams.push('game ID');
    if (!userId) missingParams.push('user ID');
    if (!userLocation) {
      missingParams.push('location');
    } else {
      if (userLocation.lat === undefined || userLocation.lat === null) missingParams.push('latitude');
      if (userLocation.lon === undefined || userLocation.lon === null) missingParams.push('longitude');
    }

    if (missingParams.length > 0) {
      const errorMsg = missingParams.includes('location') || missingParams.includes('latitude') || missingParams.includes('longitude')
        ? 'Location not available. Please enable location services and refresh the page.'
        : `Unable to reserve seat. Missing: ${missingParams.join(', ')}. Please try refreshing the page.`;
      console.error('Missing required parameters:', missingParams);
      setError(errorMsg);
      return;
    }

    try {
      setReserving(true);

      // Get the first table (for now, single table support)
      const tableId = tables.length > 0 ? tables[0].tableId : null;

      console.log('Calling seatsApi.reserveSeat with:', {
        gameId: game.id,
        userId,
        userLat: userLocation.lat,
        userLon: userLocation.lon,
        tableId,
        seatNumber
      });

      const response = await seatsApi.reserveSeat(
        game.id,
        userId,
        userLocation.lat,
        userLocation.lon,
        tableId,
        seatNumber
      );

      if (response.success) {
        // Add user to local state
        const userAvatars = ['👨', '👩', '🧑', '👱‍♂️', '👱‍♀️', '🧔', '👩‍🦰', '👨‍🦱'];
        const randomAvatar = userAvatars[Math.floor(Math.random() * userAvatars.length)];

        const newPlayer = {
          id: userId,
          name: username,
          seat: seatNumber,
          tableId: tableId,
          avatar: randomAvatar,
        };

        // Remove user from previous seat if they had one
        const updatedPlayers = players.filter((player) => player.id !== userId);
        setPlayers([...updatedPlayers, newPlayer]);
        setSelectedSeat(seatNumber);
        setShowSuccess(true);

        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Error reserving seat:', err);
      setError(err.message || 'Failed to reserve seat');
      setTimeout(() => setError(null), 3000);
    } finally {
      setReserving(false);
    }
  };

  // Oval table seat positions for 10 player seats
  // Seat 1 starts on the right (3 o'clock) and goes clockwise
  // No seat at 12 o'clock (dealer position)
  const getSeatPosition = (seatNumber) => {
    // 10 seats distributed around the oval, skipping the top (12 o'clock)
    // We divide the circle into 11 segments but only use 10 (skip top)
    // Seat 1 = right side, going clockwise
    const seatIndex = seatNumber - 1; // 0-9 for seats 1-10

    // With 11 segments, each is ~32.7 degrees
    // Position 0 would be top (12 o'clock) - skip this for dealer
    // Seats use positions 1-10 (clockwise from top-right)
    const totalSegments = 11; // 11 positions around the table
    const angleStep = 360 / totalSegments;

    // Start from position 1 (first position clockwise from top)
    // Top is -90 degrees, so position 1 is at -90 + angleStep
    const startAngle = -90 + angleStep; // ~-57.3 degrees (top-right area)
    const angleDeg = startAngle + (seatIndex * angleStep);
    const angleRad = (angleDeg * Math.PI) / 180;

    // Oval radii (as percentage of container)
    const radiusX = 38; // horizontal radius
    const radiusY = 32; // vertical radius (smaller to fit in container)

    // Calculate position on ellipse
    const x = 50 + radiusX * Math.cos(angleRad);
    const y = 50 + radiusY * Math.sin(angleRad);

    return { left: `${x}%`, top: `${y}%` };
  };

  const getPlayerAtSeat = (seatNumber) => {
    return players.find((player) => player.seat === seatNumber);
  };

  if (loading) {
    return (
      <div className="game-table-container">
        <div className="status-bar">
          <span className="time">9:41</span>
          <div className="status-icons">
            <span className="signal"></span>
            <span className="wifi"></span>
            <span className="battery"></span>
          </div>
        </div>
        <div className="loading-section">
          <div className="loading-spinner">♠</div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="game-table-container">
        <div className="status-bar">
          <span className="time">9:41</span>
          <div className="status-icons">
            <span className="signal"></span>
            <span className="wifi"></span>
            <span className="battery"></span>
          </div>
        </div>
        <div className="error-section">
          <p>No game selected</p>
        </div>
      </div>
    );
  }

  const venueName = gameInfo?.venue?.name || game.venue?.name || 'Poker Game';
  const gameType = gameInfo?.gameType || game.gameType || 'Texas Hold\'em';
  const startTime = gameInfo?.startTime || game.startTime;

  // Format start time in league timezone
  const leagueTimezone = gameInfo?.leagueTimezone || game.leagueTimezone;
  const formatStartTime = (time) => {
    if (!time) return '';
    const date = new Date(time);
    const options = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (leagueTimezone) options.timeZone = leagueTimezone;
    return date.toLocaleTimeString('en-US', options);
  };

  return (
    <div className="game-table-container">
      <div className="status-bar">
        <span className="time">9:41</span>
        <div className="status-icons">
          <span className="signal"></span>
          <span className="wifi"></span>
          <span className="battery"></span>
        </div>
      </div>

      <div className="game-header">
        <span className="player-welcome">Playing as {username}</span>
        <button className="logout-button" onClick={onLogout}>Logout</button>
      </div>

      <div className="table-section">
        <div className="poker-table">
          <div className="table-felt">
            {gameStarted ? (
              <div className="blinds-display-center">
                <div className="bar-name">{venueName}</div>
                {isBreak ? (
                  <>
                    <div className="break-label">BREAK</div>
                    <div className={`timer-countdown ${isPaused ? 'paused' : ''}`}>
                      {formatTime(timeRemaining)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="blinds-values">
                      ${currentBlind?.smallBlind || 0} / ${currentBlind?.bigBlind || 0}
                    </div>
                    {currentBlind?.ante > 0 && (
                      <div className="ante-value">Ante: ${currentBlind.ante}</div>
                    )}
                    <div className={`timer-countdown ${isPaused ? 'paused' : ''}`}>
                      {formatTime(timeRemaining)}
                    </div>
                    <div className="level-indicator">Level {currentLevel}</div>
                  </>
                )}
                {isPaused && <div className="paused-indicator">PAUSED</div>}
              </div>
            ) : (
              <div className="table-info-center">
                <div className="bar-name">{venueName}</div>
                <div className="game-details">
                  <span className="game-type-badge">{gameType}</span>
                  {startTime && <span className="start-time">{formatStartTime(startTime)}</span>}
                </div>
                {currentBlind && (
                  <div className="blinds-info">
                    ${currentBlind?.smallBlind || 0} / ${currentBlind?.bigBlind || 0}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dealer position at top center (12 o'clock) */}
          <div className="dealer" style={{ left: '50%', top: '12%' }}>
            <div className="dealer-avatar">🎩</div>
            <div className="dealer-label">{gameInfo?.dealer?.name || 'Dealer'}</div>
          </div>

          {/* Render 10 player seats - Seat 1 on right, going clockwise, skipping 12 o'clock */}
          {[...Array(totalSeats)].map((_, index) => {
            const seatNumber = index + 1; // Player seat numbers 1-10
            const player = getPlayerAtSeat(seatNumber);
            const position = getSeatPosition(seatNumber);
            const isOccupied = !!player;
            const isCurrentUser = player?.id === userId;

            return (
              <div
                key={seatNumber}
                className={`seat ${isOccupied ? 'occupied' : 'empty'} ${isCurrentUser ? 'current-user' : ''} ${reserving ? 'disabled' : ''}`}
                style={position}
                onClick={() => handleSeatClick(seatNumber)}
              >
                {isOccupied ? (
                  <>
                    <div className="player-avatar">{player.avatar}</div>
                    <div className="player-name">{formatPlayerName(player.name)}</div>
                    {isCurrentUser && <div className="user-indicator">You</div>}
                  </>
                ) : (
                  <>
                    <div className="empty-seat-icon">+</div>
                    <div className="seat-label">Seat {seatNumber}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="player-count">
          <span className="count-badge">
            {players.length}/{totalSeats} Players
          </span>
        </div>
      </div>

      {showSuccess && (
        <div className="success-toast">Seat {selectedSeat} reserved!</div>
      )}

      {error && <div className="error-toast">{error}</div>}
    </div>
  );
}

export default GameTable;
